"use client"

import React, { useState, useRef } from "react"
import { X, Search, User, Edit, Plus, Trash2, Loader2, RefreshCw, UserPlus, UserMinus, ChevronDown, ChevronRight, Mail, Zap, Settings, CalendarPlus, CalendarX2, Calendars, CalendarCog } from "lucide-react"
import DOMPurify from "isomorphic-dompurify"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuditHistory } from "@/hooks/use-audit-history"
import { useAutomationEmails } from "@/hooks/use-automation-emails"
import { useWorkflow } from "@/context/workflow-context-simple"
import { useLanguage } from "@/context/language-context"
import { useFeatureFlags } from '@/contexts/feature-flag-context'
import {
  resolveStageTitle,
  resolveFieldDisplayName,
  resolveClientRequestFieldValue,
  formatAuditValue
} from "@/utils/audit-helpers"
import type { AuditHistoryDocument, UpdateEntry } from "@/types/audit"
import { translations } from "@/translations"

interface AuditHistoryPanelProps {
  isOpen: boolean
  onClose: () => void
  itemId: string
  itemTitle?: string
  entityType?: string
}

const actionIconMap = {
  'Create': Plus,
  'Update': Edit,
  'Edit': Edit,
  'Delete': Trash2,
  'StatusChange': RefreshCw,
  'Assignment': UserPlus,
  'Unassignment': UserMinus
}

const clientRequestIconMap: Record<string, React.ReactNode> = {
  'Extension': <CalendarPlus className="h-4 w-4 text-green-500" />,
  'Termination': <CalendarX2 className="h-4 w-4 text-red-500" />,
  'Redeployment': <Calendars className="h-4 w-4 text-purple-500" />,
  'General': <CalendarCog className="h-4 w-4 text-orange-500" />,
}
const clientRequestDefaultIcon = <CalendarCog className="h-4 w-4 text-gray-500" />

export function AuditHistoryPanel({ isOpen, onClose, itemId, itemTitle, entityType }: AuditHistoryPanelProps) {
  const { records, groupedRecords, loading, hasMore, searchTerm, setSearchTerm, loadMore, refresh: refreshHistory, clearError } = useAuditHistory(itemId, entityType)
  const { configuration, codeValues } = useWorkflow()
  const { language } = useLanguage()
  const { isFeatureEnabled } = useFeatureFlags()

  // Automation Emails tab is gated by TWO conditions — BOTH must be true to show
  // it; if either is false the tab is hidden (across every entity family that
  // shares this panel: WorkOrder, ClientRequest, VMS, ATS):
  //   1. The `automationsLinkEnabled` feature flag (kill-switch for the feature).
  //   2. The `AutomationsLink` authorized action (per-user/role permission).
  // Same action-code pattern as ViewVmsSettings/ViewVmsRequests: map
  // authorizedActions (string | { actionCode }) to codes and check membership.
  // When hidden, the panel stays on Change History.
  const authorizedActionCodes = (configuration?.authorizedActions || []).map(
    (a: any) => (typeof a === 'string' ? a : a.actionCode)
  )
  const canViewAutomationEmails = authorizedActionCodes.includes('AutomationsLink')
  const showAutomationEmailsTab = isFeatureEnabled('automationsLinkEnabled') && canViewAutomationEmails

  const [activeTab, setActiveTab] = useState<'history' | 'automation'>('history')

  // Defensive: if the tab is somehow active but it shouldn't be shown (e.g. config
  // or flags load after mount, or the panel is reused), snap back to history so we
  // never render the gated content or fire its fetch.
  React.useEffect(() => {
    if (!showAutomationEmailsTab && activeTab === 'automation') {
      setActiveTab('history')
    }
  }, [showAutomationEmailsTab, activeTab])
  const { workOrders: emailWorkOrders, emails, loading: emailsLoading, error: emailsError, refresh: refreshEmails } = useAutomationEmails(itemId, activeTab === 'automation' && isOpen, isOpen, entityType)
  const [isSearching, setIsSearching] = useState(false)
  const [expandedRecords, setExpandedRecords] = useState<Set<string>>(new Set())
  const [expandedWorkOrders, setExpandedWorkOrders] = useState<Set<string>>(new Set())
  const [selectedStatus, setSelectedStatus] = useState<string>('')
  const [selectedUser, setSelectedUser] = useState<string>('')
  const [selectedDateFilter, setSelectedDateFilter] = useState<string>('all')
  const [customStartDate, setCustomStartDate] = useState<string>('')
  const [customEndDate, setCustomEndDate] = useState<string>('')
  const [visibleRecordCount, setVisibleRecordCount] = useState(5)
  const [isVisible, setIsVisible] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(new Set())
  const [expandedEmailContent, setExpandedEmailContent] = useState<Set<string>>(new Set())
  const [expandedEmailWorkOrders, setExpandedEmailWorkOrders] = useState<Set<string>>(new Set())
  const [emailSearchTerm, setEmailSearchTerm] = useState('')
  const [selectedEmailStage, setSelectedEmailStage] = useState<string>('')
  const [selectedEmailRecipient, setSelectedEmailRecipient] = useState<string>('')
  const [selectedEmailDateFilter, setSelectedEmailDateFilter] = useState<string>('all')
  const [customEmailStartDate, setCustomEmailStartDate] = useState<string>('')
  const [customEmailEndDate, setCustomEmailEndDate] = useState<string>('')
  
  // Get translations for current language
  const t = translations[language as keyof typeof translations] || translations.en

  // Helper function to detect if a field is a stage field
  const isStageField = (fieldName: string): boolean => {
    return fieldName?.toLowerCase() === 'stage';
  }

  // ── Nested-object diff aggregation ─────────────────────────────────────────
  //
  // The audit service flattens nested-object changes (AssignedToUser, Client,
  // Manager, Recruiter, ...) to one diff per leaf field — e.g. swapping the
  // assigned user produces five rows (Email, FullName, FirstName, LastName,
  // ProfileId), each with PropertyPath="AssignedToUser" and FieldName=<leaf>.
  // The renderer keys off FieldName so the user sees five unhelpful "Email" /
  // "FullName" rows (or for ClientRequest, the rows get filtered out because
  // the leaf names aren't in CLIENT_REQUEST_FIELD_NAMES).
  //
  // Fixing this at the BE (FBO.Services.Common.Audit.Core.Audit) would change
  // contract for every consumer (other services, exports, future reporting),
  // so we collapse on the FE instead. For each non-empty PropertyPath group,
  // we emit one combined entry where FieldName = parent path and old/new
  // values come from the "headline" leaf — the leaf that best summarises the
  // change for a human (FullName for users, Name for client, etc.). Diffs
  // with empty PropertyPath (root-level fields) pass through unchanged.
  const STRUCTURED_HEADLINE_FIELD: Record<string, string> = {
    AssignedToUser:  'FullName',
    Client:          'Name',
    Manager:         'FullName',
    Recruiter:       'FullName',
    PrimaryContact:  'FullName',
    PrimaryJobOwner: 'FullName',
  }

  const collapseNestedDiffs = (differences: UpdateEntry[] | undefined | null): UpdateEntry[] => {
    if (!Array.isArray(differences) || differences.length === 0) return differences ?? []

    const passthrough: UpdateEntry[] = []
    // Use insertion-ordered Map so collapsed rows render in the same order as the
    // first leaf appeared in the original list.
    const groups = new Map<string, UpdateEntry[]>()

    // Strip array-index segments from the path. When a nested object is *added*
    // (e.g. Client goes from null → {FboId, ObCode, Name}), the diff engine
    // (jsondiffpatch via FBO.Services.Common.Audit) represents the added value as a
    // single-element array, so the leaf paths come through as "Client/0" instead of
    // "Client". Without this, the collapsed entry's fieldName becomes "Client/0",
    // which matches no column config (STRUCTURED_HEADLINE_FIELD nor resolveFieldDisplayName)
    // and the whole change silently drops from the History tab. Normalising "/<n>" →
    // "" makes the add case collapse identically to the modify case ("Client").
    const normalizePath = (p: string) => p.replace(/\/\d+(?=\/|$)/g, '')

    for (const diff of differences) {
      if (!diff) continue
      const path = normalizePath(diff.propertyPath || '')
      if (!path) {
        passthrough.push(diff)
        continue
      }
      const bucket = groups.get(path) ?? []
      bucket.push(diff)
      groups.set(path, bucket)
    }

    const collapsed: UpdateEntry[] = []
    for (const [path, bucket] of groups) {
      // Prefer the configured headline; fall back to common-name heuristics; last
      // resort is the first leaf so we never drop the change entirely.
      const headlineFieldName = STRUCTURED_HEADLINE_FIELD[path]
      let headline: UpdateEntry | undefined
      if (headlineFieldName) {
        headline = bucket.find(d => d.fieldName === headlineFieldName)
      }
      if (!headline) headline = bucket.find(d => d.fieldName === 'FullName')
      if (!headline) headline = bucket.find(d => d.fieldName === 'Name')
      if (!headline) headline = bucket.find(d => d.fieldName === 'Title')
      if (!headline) headline = bucket[0]

      collapsed.push({
        ...headline,
        propertyPath: '',  // present as a top-level field after collapse
        fieldName: path,   // the parent path becomes the rendered field name
      })
    }

    return [...passthrough, ...collapsed]
  }

  // Handle slide animation when panel opens/closes
  React.useEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM is ready before starting animation
      const timer = setTimeout(() => setIsVisible(true), 10)
      return () => clearTimeout(timer)
    } else {
      setIsVisible(false)
    }
  }, [isOpen])

  // Auto-expand all records and WorkOrder groups by default to show changes immediately
  React.useEffect(() => {
    if (records && records.length > 0) {
      const allRecordIds = records.map(record => record.id).filter(Boolean) as string[]
      setExpandedRecords(new Set(allRecordIds))
    }
    if (groupedRecords && groupedRecords.length > 0) {
      const allWorkOrderNumbers = groupedRecords.map(group => group.workOrderNumber).filter(Boolean)
      setExpandedWorkOrders(new Set(allWorkOrderNumbers))
    }
  }, [records, groupedRecords])

  // Auto-expand all email WorkOrder groups by default
  React.useEffect(() => {
    if (emailWorkOrders && emailWorkOrders.length > 0) {
      const allEmailWorkOrderNumbers = emailWorkOrders.map(wo => wo.workOrderNumber).filter(Boolean)
      setExpandedEmailWorkOrders(new Set(allEmailWorkOrderNumbers))
    }
  }, [emailWorkOrders])

  // Handle click outside to close panel.
  //
  // Radix-based popovers (the shadcn Select dropdown used inside this panel,
  // tooltips, dialogs, etc.) render their content in a portal at the document
  // body level — *outside* panelRef. Without the portal guard below, clicking
  // on a Select item or any popper-positioned content would be treated as a
  // click outside the panel and would close the entire drawer on the second
  // interaction (the first opens the Select, the second selects an item and
  // also dismisses us). Skip mousedown events whose target lives inside any
  // Radix portal wrapper so legitimate popper interactions don't close us.
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (panelRef.current && panelRef.current.contains(target)) return

      // Radix portals tag their wrappers with these data-attributes; if the
      // click landed inside one (Select dropdown, Popover, Tooltip, etc.),
      // leave the drawer open.
      if (target instanceof Element && target.closest(
        '[data-radix-popper-content-wrapper], [data-radix-select-content], [data-radix-popover-content], [role="listbox"]'
      )) {
        return
      }

      onClose()
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  // Shared utility functions
  const getStageOptions = React.useCallback(() => {
    if (!configuration?.stageConfigurations) return []
    return configuration.stageConfigurations.map(stage => ({
      value: stage.stageCode,
      label: language === 'fr' ? stage.titleFrench : stage.titleEnglish
    }))
  }, [configuration?.stageConfigurations, language])

  const getDateFilterOptions = React.useCallback(() => [
    { value: 'all', label: t.all },
    { value: 'lastWeek', label: t.lastWeek },
    { value: 'lastMonth', label: t.lastMonth },
    { value: '90days', label: t['90days'] },
    { value: 'lastYear', label: t.lastYear },
    { value: 'custom', label: t.custom }
  ], [t])

  const isWithinDateRange = React.useCallback((recordDate: string, filterType: string, customStartDate?: string, customEndDate?: string): boolean => {
    if (filterType === 'all') return true
    if (!recordDate) return false

    // Date strings from the wire come in two flavours:
    //  - WorkOrder/Assignment audit dates often serialize with a trailing Z
    //    (UTC zulu), e.g. "2026-05-15T12:34:56Z".
    //  - VMS audit dates flow through DateTimeOffset → System.Text.Json,
    //    which emits the offset form "2026-05-15T12:34:56+00:00".
    //
    // The previous logic appended `Z` to anything not already ending in Z,
    // which turned the offset form into "...+00:00Z" — an invalid date that
    // every browser parses to NaN. The filter then dropped every VMS audit
    // row, even though the values were perfectly valid ISO-8601.
    //
    // Detect any timezone designator (Z, +HH:MM, -HH:MM, +HHMM, -HHMM) and
    // only append Z when the string is truly naked. ISO-8601 with an explicit
    // offset is already understood by `new Date()`, so we leave it as-is.
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(recordDate)
    const utcDate = hasTimezone ? recordDate : `${recordDate}Z`
    const recordDateTime = new Date(utcDate)

    if (isNaN(recordDateTime.getTime())) return false
    
    const now = new Date()
    const diffInDays = Math.floor((now.getTime() - recordDateTime.getTime()) / (1000 * 60 * 60 * 24))
    
    switch (filterType) {
      case 'lastWeek':
        return diffInDays <= 7
      case 'lastMonth':
        return diffInDays <= 31
      case '90days':
        return diffInDays <= 90
      case 'lastYear':
        return diffInDays <= 365
      case 'custom':
        if (customStartDate && customEndDate) {
          const startDate = new Date(customStartDate)
          const endDate = new Date(customEndDate)
          endDate.setHours(23, 59, 59, 999)
          return recordDateTime >= startDate && recordDateTime <= endDate
        }
        return true
      default:
        return true
    }
  }, [])

  // Helper function to get display name (prioritize full name over username)
  const getUserDisplayName = (record: any) => {
    return record.createdByUserFullName || record.createdByUserName
  }

  // Get available stages from workflow configuration (shared for both tabs)
  const availableStages = React.useMemo(() => getStageOptions(), [getStageOptions])

  // Get distinct users from records (for Change History)
  const availableUsers = React.useMemo(() => {
    if (!records) return []
    const uniqueUsers = new Set<string>()
    records.forEach(record => {
      const displayName = getUserDisplayName(record)
      if (displayName) {
        uniqueUsers.add(displayName)
      }
    })
    return Array.from(uniqueUsers).sort().map(user => ({
      value: user,
      label: user
    }))
  }, [records])

  // Get distinct email recipients from emails (for Automation Emails)
  const availableEmailRecipients = React.useMemo(() => {
    if (!emails) return []
    const uniqueRecipients = new Set<string>()
    emails.forEach(email => {
      if (email.sentTo) {
        uniqueRecipients.add(email.sentTo)
      }
    })
    return Array.from(uniqueRecipients).sort().map(recipient => ({
      value: recipient,
      label: recipient
    }))
  }, [emails])

  // Get date filter options (shared)
  const dateFilterOptions = React.useMemo(() => getDateFilterOptions(), [getDateFilterOptions])

  // Filter records based on search term, status, user, and date
  const filteredRecords = React.useMemo(() => {
    if (!records) return []
    
    return records.filter(record => {
      // Status filter
      if (selectedStatus && record.entityStatusCode !== selectedStatus) {
        return false
      }
      
      // User filter
      if (selectedUser && getUserDisplayName(record) !== selectedUser) {
        return false
      }
      
      // Date filter — fall back to PascalCase too: the VMS audit endpoint
      // returns records via a server-side wrapper whose JSON casing differs
      // from the original WorkOrder shape, and without this fallback the
      // filter silently treated the record as undated and always kept it.
      const recordCreatedAt = (record as any).createdDateTime ?? (record as any).CreatedDateTime
      if (selectedDateFilter !== 'all') {
        if (!recordCreatedAt) {
          return false
        }
        if (!isWithinDateRange(recordCreatedAt, selectedDateFilter, customStartDate, customEndDate)) {
          return false
        }
      }
      
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        
        // Search in field names (both raw field names and resolved display names)
        const resolveValueForSearch = (value: any, fieldName: string) => {
          if (value == null) return ''
          const strValue = String(value)
          if (isStageField(fieldName)) return resolveStageTitle(strValue, configuration, language)
          if (record.entityType === 'ClientRequest') {
            return resolveClientRequestFieldValue(fieldName, strValue, language) ?? (strValue.includes('T') ? strValue.split('T')[0] : strValue)
          }
          return strValue.includes('T') ? strValue.split('T')[0] : strValue
        }
        const hasMatchingField = record.differences?.some(difference => {
          // Check raw field name (handles "Stage" and other fields not in config)
          const rawFieldNameMatch = difference.fieldName?.toLowerCase().includes(searchLower)

          // Check resolved display name (handles localized field names)
          const fieldDisplayName = resolveFieldDisplayName(difference.fieldName, configuration, language, difference.propertyPath, record.entityType)
          const displayNameMatch = fieldDisplayName && fieldDisplayName.toLowerCase().includes(searchLower)

          // Check old and new values using resolved display strings
          const oldValueMatch = difference.oldValue != null && resolveValueForSearch(difference.oldValue, difference.fieldName).toLowerCase().includes(searchLower)
          const newValueMatch = difference.newValue != null && resolveValueForSearch(difference.newValue, difference.fieldName).toLowerCase().includes(searchLower)

          return rawFieldNameMatch || displayNameMatch || oldValueMatch || newValueMatch
        })

        // Search in username (prioritize full name)
        const hasMatchingUser = getUserDisplayName(record)?.toLowerCase().includes(searchLower)

        // Search in status/stage title
        const stageTitle = resolveStageTitle(record.entityStatusCode, configuration, language, record.entityType)
        const hasMatchingStatus = stageTitle.toLowerCase().includes(searchLower)
        
        // Search in comment/notes
        const hasMatchingComment = record.comment?.toLowerCase().includes(searchLower)
        
        return hasMatchingField || hasMatchingUser || hasMatchingStatus || hasMatchingComment
      }
      
      return true
    })
  }, [records, searchTerm, selectedStatus, selectedUser, selectedDateFilter, customStartDate, customEndDate, configuration, language, isWithinDateRange])

  // Fold an action note into its status-change row.
  //
  // When the unified-envelope flag is on, a bulk/lifecycle action (e.g. "On Hold", "Cancel")
  // produces TWO audit rows for one operator action: the status-change row the envelope writes
  // from the change feed (has the Status diff, no comment) and a comment-only row the service
  // writes inline (has the note, no diff). They land a few seconds apart. Rather than show the
  // note as its own card next to the status change, attach it to the matching status-change row
  // (same record + same user, within a short window) and drop the standalone note row, so History
  // shows a single entry. Genuine standalone notes (no nearby status change) are left untouched.
  const mergedRecords = React.useMemo(() => {
    if (!filteredRecords || filteredRecords.length === 0) return filteredRecords ?? []

    const getTime = (r: any) => {
      const raw = r?.createdDateTime ?? r?.CreatedDateTime
      const t = raw ? new Date(raw).getTime() : NaN
      return Number.isNaN(t) ? null : t
    }
    const getGrouping = (r: any) => r?.groupingId ?? r?.GroupingId ?? r?.entityGuid ?? r?.EntityGuid ?? ''
    // Match on the login (createdByUserName), not the display name: the envelope status row often
    // has an empty createdByUserFullName while the inline note row carries it, so full names differ.
    const getUserKey = (r: any) => (r?.createdByUserName ?? r?.CreatedByUserName ?? '').toLowerCase()

    const hasMeaningfulDifferences = (record: any) =>
      Array.isArray(record?.differences) && record.differences.some((difference: any) => {
        if (!difference || !difference.fieldName) return false
        if (difference.fieldName.toLowerCase().includes('lastmodifieddatetime')) return false
        if (isStageField(difference.fieldName)) return true
        return resolveFieldDisplayName(difference.fieldName, configuration, language, difference.propertyPath, record.entityType) !== null
      })

    const MERGE_WINDOW_MS = 2 * 60 * 1000 // 2 minutes — generous cover for change-feed lag

    const commentOnly = filteredRecords.filter((r: any) => r?.comment && !hasMeaningfulDifferences(r))
    if (commentOnly.length === 0) return filteredRecords

    const absorbedIds = new Set<string>()
    const commentByTargetId = new Map<string, string>()

    for (const note of commentOnly) {
      const noteTime = getTime(note)
      const noteGroup = getGrouping(note)
      const noteUser = getUserKey(note)
      let best: { id: string; dist: number } | null = null
      for (const candidate of filteredRecords as any[]) {
        if (candidate === note || !candidate?.id) continue
        if (commentByTargetId.has(candidate.id)) continue   // one note per target row
        if (candidate.comment) continue                      // target already carries its own note
        if (!hasMeaningfulDifferences(candidate)) continue
        if (getGrouping(candidate) !== noteGroup) continue
        if (getUserKey(candidate) !== noteUser) continue
        const candTime = getTime(candidate)
        if (noteTime == null || candTime == null) continue
        const dist = Math.abs(candTime - noteTime)
        if (dist > MERGE_WINDOW_MS) continue
        if (!best || dist < best.dist) best = { id: candidate.id, dist }
      }
      if (best) {
        commentByTargetId.set(best.id, note.comment as string)
        absorbedIds.add(note.id as string)
      }
    }

    if (absorbedIds.size === 0) return filteredRecords

    return filteredRecords
      .filter((r: any) => !(r?.id && absorbedIds.has(r.id)))
      .map((r: any) => (r?.id && commentByTargetId.has(r.id)) ? { ...r, comment: commentByTargetId.get(r.id) } : r)
  }, [filteredRecords, configuration, language, isStageField])

  // Filter out records that only have LastModifiedDateTime changes (for accurate counting and display)
  const displayableRecords = React.useMemo(() => {
    if (!mergedRecords) return []

    return mergedRecords.filter(record => {
      if (!record || !record.id) return false;
      
      // Check if there are any valid field changes to display (same logic as in renderAuditRecord)
      const hasValidChanges = record.differences && Array.isArray(record.differences) && record.differences.length > 0 && 
        record.differences.some(difference => {
          if (!difference || !difference.fieldName) return false;
          
          // Exclude LastModifiedDateTime fields as they're not meaningful to users
          if (difference.fieldName.toLowerCase().includes('lastmodifieddatetime')) return false;
          
          // Always show stage fields
          if (isStageField(difference.fieldName)) return true;
          
          // For other fields, check if they have field configurations
          const fieldDisplayName = resolveFieldDisplayName(difference.fieldName, configuration, language, difference.propertyPath, record.entityType);
          return fieldDisplayName !== null;
        });

      // Include record if it has valid changes or a comment
      return hasValidChanges || record.comment;
    });
  }, [mergedRecords, configuration, language, isStageField]);

  // Create visible records based on search state and visible count
  const visibleRecords = React.useMemo(() => {
    if (!displayableRecords) return []
    
    // If searching or filtering, show all results
    if (searchTerm || selectedStatus || selectedUser || selectedDateFilter !== 'all') {
      return displayableRecords
    }
    
    // Otherwise, limit to visible count
    return displayableRecords.slice(0, visibleRecordCount)
  }, [displayableRecords, searchTerm, selectedStatus, selectedUser, selectedDateFilter, visibleRecordCount])

  // Reset visible count when search or filters change
  React.useEffect(() => {
    if (searchTerm || selectedStatus || selectedUser || selectedDateFilter !== 'all') {
      // When searching/filtering, show all results
      setVisibleRecordCount(displayableRecords.length || 5)
    } else {
      // When clearing search/filters, reset to initial count
      setVisibleRecordCount(5)
    }
  }, [searchTerm, selectedStatus, selectedUser, selectedDateFilter, displayableRecords.length])

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    setIsSearching(true)
    // Reset and reload with new search term
    setTimeout(() => setIsSearching(false), 500)
  }

  const handleShowMore = () => {
    setVisibleRecordCount(prev => prev + 5)
  }

  const handleLoadMore = async () => {
    await loadMore()
  }

  // Shared clear filters functions
  const clearHistoryFilters = () => {
    setSelectedStatus('')
    setSelectedUser('')
    setSelectedDateFilter('all')
    setCustomStartDate('')
    setCustomEndDate('')
    setSearchTerm('')
    setVisibleRecordCount(5) // Reset to initial count when clearing filters
  }

  const clearEmailFilters = () => {
    setSelectedEmailStage('')
    setSelectedEmailRecipient('')
    setSelectedEmailDateFilter('all')
    setCustomEmailStartDate('')
    setCustomEmailEndDate('')
    setEmailSearchTerm('')
  }

  // Entity families that don't have meaningful workflow stages on their audit
  // rows (VMS request/email rows go through a single fixed stage, settings
  // audit doesn't have stages at all). For these we hide the Stage filter
  // entirely and shrink the filter grid from 3 cols to 2 so the remaining
  // two filters stay readable instead of leaving an empty slot.
  const stageFilterApplies = entityType !== 'VmsRequest' && entityType !== 'VmsEmail'

  // Shared filter component structure
  const renderFilterSection = (
    searchPlaceholder: string,
    searchValue: string,
    onSearchChange: (value: string) => void,
    stageOptions: Array<{value: string, label: string}>,
    stageValue: string,
    onStageChange: (value: string) => void,
    stageLabel: string,
    secondFilterOptions: Array<{value: string, label: string}>,
    secondFilterValue: string,
    onSecondFilterChange: (value: string) => void,
    secondFilterLabel: string,
    dateValue: string,
    onDateChange: (value: string) => void,
    customStartDate: string,
    onCustomStartDateChange: (value: string) => void,
    customEndDate: string,
    onCustomEndDateChange: (value: string) => void,
    showClearButton: boolean,
    onClearFilters: () => void
  ) => (
    <div className="p-4 space-y-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>
      
      {/* Filters */}
      {/*
        Switched from native <select> to the shadcn Select used elsewhere
        (vms-settings-panel, client-request-modal, edit-field-modal) so the
        styling matches the rest of the app. Two adapters were required:
          - Empty string `""` was the "All" sentinel for the stage + second
            filters; shadcn SelectItem rejects empty values, so we map to/from
            a "__all__" sentinel here.
          - shadcn fires onValueChange(value: string) instead of native
            onChange(event); the outer callback contract is unchanged.
      */}
      <div className={`grid ${stageFilterApplies ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
        {/* Stage Filter — only rendered for entities that actually go through
            multiple workflow stages. VMS requests + emails are single-stage and
            settings audit has no stages, so we drop the column there. */}
        {stageFilterApplies && (
          <div>
            <Select
              value={stageValue === '' ? '__all__' : stageValue}
              onValueChange={(v) => onStageChange(v === '__all__' ? '' : v)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={stageLabel} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{stageLabel}</SelectItem>
                {stageOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Second Filter (Users by default; can be repurposed by host) */}
        <div>
          <Select
            value={secondFilterValue === '' ? '__all__' : secondFilterValue}
            onValueChange={(v) => onSecondFilterChange(v === '__all__' ? '' : v)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={secondFilterLabel} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{secondFilterLabel}</SelectItem>
              {secondFilterOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date Filter — already uses non-empty values ('all', 'lastWeek',
            etc.) so no sentinel mapping is needed. */}
        <div>
          <Select value={dateValue} onValueChange={(v) => onDateChange(v)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {dateFilterOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Custom Date Range Inputs */}
      {dateValue === 'custom' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Input
              type="date"
              value={customStartDate}
              onChange={(e) => onCustomStartDateChange(e.target.value)}
              placeholder={t.startDate}
            />
          </div>
          <div>
            <Input
              type="date"
              value={customEndDate}
              onChange={(e) => onCustomEndDateChange(e.target.value)}
              placeholder={t.endDate}
            />
          </div>
        </div>
      )}
      
      {/* Clear Filters Button */}
      {showClearButton && (
        <Button
          variant="outline"
          size="sm"
          onClick={onClearFilters}
          className="w-full"
        >
          {t.clearAll}
        </Button>
      )}
    </div>
  )

  const toggleRecordExpansion = (recordId: string) => {
    const newExpanded = new Set(expandedRecords)
    if (newExpanded.has(recordId)) {
      newExpanded.delete(recordId)
    } else {
      newExpanded.add(recordId)
    }
    setExpandedRecords(newExpanded)
  }

  const toggleWorkOrderGroupExpansion = (workOrderNumber: string) => {
    const newExpanded = new Set(expandedWorkOrders)
    if (newExpanded.has(workOrderNumber)) {
      newExpanded.delete(workOrderNumber)
    } else {
      newExpanded.add(workOrderNumber)
    }
    setExpandedWorkOrders(newExpanded)
  }

  const getActionIconComponent = (action: string) => {
    const IconComponent = actionIconMap[action as keyof typeof actionIconMap] || Edit
    return <IconComponent className="h-4 w-4" />
  }

  const toggleEmailExpansion = (emailKey: string) => {
    const newExpanded = new Set(expandedEmails)
    if (newExpanded.has(emailKey)) {
      newExpanded.delete(emailKey)
    } else {
      newExpanded.add(emailKey)
    }
    setExpandedEmails(newExpanded)
  }

  const toggleEmailWorkOrderGroupExpansion = (workOrderNumber: string) => {
    const newExpanded = new Set(expandedEmailWorkOrders)
    if (newExpanded.has(workOrderNumber)) {
      newExpanded.delete(workOrderNumber)
    } else {
      newExpanded.add(workOrderNumber)
    }
    setExpandedEmailWorkOrders(newExpanded)
  }

  const toggleEmailContentExpansion = (emailKey: string) => {
    const newExpanded = new Set(expandedEmailContent)
    if (newExpanded.has(emailKey)) {
      newExpanded.delete(emailKey)
    } else {
      newExpanded.add(emailKey)
    }
    setExpandedEmailContent(newExpanded)
  }

  const filteredEmails = React.useMemo(() => {
    if (!emails || emails.length === 0) return []
    
    return emails.filter(email => {
      // Stage filter
      if (selectedEmailStage && email.stage !== selectedEmailStage) {
        return false
      }
      
      // Recipient filter
      if (selectedEmailRecipient && email.sentTo !== selectedEmailRecipient) {
        return false
      }
      
      // Date filter
      if (selectedEmailDateFilter !== 'all' && email.sentAt) {
        if (!isWithinDateRange(email.sentAt, selectedEmailDateFilter, customEmailStartDate, customEmailEndDate)) {
          return false
        }
      }
      
      // Search filter
      if (emailSearchTerm) {
        const searchLower = emailSearchTerm.toLowerCase()
        return email.subject?.toLowerCase().includes(searchLower) ||
               email.sentTo?.toLowerCase().includes(searchLower) ||
               email.sentFrom?.toLowerCase().includes(searchLower) ||
               email.emailBody?.toLowerCase().includes(searchLower)
      }
      
      return true
    })
  }, [emails, emailSearchTerm, selectedEmailStage, selectedEmailRecipient, selectedEmailDateFilter, customEmailStartDate, customEmailEndDate, isWithinDateRange])

  const getTimezoneAbbreviation = (date: Date) => {
    const locale = language === 'fr' ? 'fr-FR' : 'en-US'
    const timezoneName = date.toLocaleTimeString(locale, { timeZoneName: 'short' }).split(' ').pop()
    return timezoneName || ''
  }

  const formatEmailDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return ''
    
    const utcDate = dateTimeString.endsWith('Z') ? dateTimeString : `${dateTimeString}Z`
    const date = new Date(utcDate)
    
    if (isNaN(date.getTime())) return dateTimeString
    
    const dateStr = date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')
    const timeStr = date.toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: language !== 'fr'
    })
    const timezone = getTimezoneAbbreviation(date)
    return `${dateStr} ${timeStr} (${timezone})`
  }

  const formatEmailDateOnly = (dateTimeString: string) => {
    if (!dateTimeString) return ''
    
    const utcDate = dateTimeString.endsWith('Z') ? dateTimeString : `${dateTimeString}Z`
    const date = new Date(utcDate)
    
    if (isNaN(date.getTime())) return dateTimeString
    
    const dateStr = date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')
    const timezone = getTimezoneAbbreviation(date)
    return `${dateStr} (${timezone})`
  }

  const resolveEmailStageTitle = (stageCode: string) => {
    if (!configuration?.stageConfigurations) return stageCode
    const stage = configuration.stageConfigurations.find(s => s.stageCode === stageCode)
    return stage ? (language === 'fr' ? stage.titleFrench : stage.titleEnglish) : stageCode
  }

  // Helper function to estimate lines in HTML content
  const estimateHtmlLines = (htmlString: string): number => {
    if (!htmlString) return 0
    // Strip HTML to get plain text
    const plainText = htmlString.replace(/<[^>]*>/g, '')
    
    // Count explicit line breaks
    const lineBreaks = (plainText.match(/\n/g) || []).length
    
    // Estimate lines based on text length (roughly 80 characters per line)
    const estimatedLines = Math.ceil(plainText.length / 80)
    
    return Math.max(lineBreaks + 1, estimatedLines)
  }

  // Function to highlight search matches in text
  const highlightSearchMatch = (text: string, searchTerm: string) => {
    if (!searchTerm) return text
    
    // Trim the search term to avoid extra spaces
    const trimmedSearchTerm = searchTerm.trim()
    if (!trimmedSearchTerm) return text
    
    const regex = new RegExp(`(${trimmedSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    const parts = text.split(regex)
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 px-1 rounded">{part}</mark>
      ) : part
    )
  }

  // Helper function to render a single email item
  const renderEmailItem = (email: any, index: number, keyPrefix: string = '') => {
    // Create stable key using keyPrefix (workOrderNumber), sentAt, and index for uniqueness
    const emailKey = keyPrefix ? `${keyPrefix}:${email.sentAt}:${index}` : `${email.sentAt}:${index}`
    const isExpanded = expandedEmails.has(emailKey)
    const isContentExpanded = expandedEmailContent.has(emailKey)
    const stageTitle = resolveEmailStageTitle(email.stage)
    const estimatedLines = estimateHtmlLines(email.emailBody)
    const shouldShowMoreButton = estimatedLines > 10
    
    return (
      <div key={emailKey} className="border-b border-gray-100 last:border-b-0">
        {/* Collapsed State - Compact Grid Layout */}
        <div 
          className="grid grid-cols-[180px_180px_100px_1fr_100px] gap-3 items-center p-3 pl-8 hover:bg-gray-50 cursor-pointer"
          onClick={() => toggleEmailExpansion(emailKey)}
          title={isExpanded ? t.clickToCollapse : t.clickToExpand}
        >
          {!isExpanded ? (
            <>
              {/* To column */}
              <div className="text-sm truncate">
                <span className="text-gray-500">{t.to}:</span> <span className="text-gray-900 font-medium">{email.sentTo}</span>
              </div>
              
              {/* SentAt column - Full date/time */}
              <div className="text-sm text-gray-600 truncate">
                {formatEmailDateTime(email.sentAt)}
              </div>
              
              {/* Stage column */}
              <div className="flex justify-center">
                <span className="text-xs text-blue-600 font-medium">
                  {stageTitle}
                </span>
              </div>
              
              {/* Subject column */}
              <div className="text-sm truncate">
                <span className="text-gray-500">{t.subject}:</span> <span className="text-gray-900">{email.subject}</span>
              </div>
              
              {/* Chevron column */}
              <div className="flex justify-end">
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                  isExpanded ? 'rotate-180' : 'rotate-0'
                }`} />
              </div>
            </>
          ) : (
            <>
              {/* Expanded state - stage next to chevron */}
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div className="flex justify-end items-center">
                <span className="text-xs text-blue-600 font-medium mr-2 flex-shrink-0">
                  {stageTitle}
                </span>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${
                  isExpanded ? 'rotate-180' : 'rotate-0'
                }`} />
              </div>
            </>
          )}
        </div>
        
        {/* Expanded State */}
        <div 
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="pl-8 pr-3 pb-3">
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              {/* Email Header */}
              <div className="mb-3 pb-3 border-b border-gray-200">
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center">
                    <span className="text-gray-500 font-medium w-16">{t.from}:</span>
                    <span className="text-gray-900">{email.sentFrom}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 font-medium w-16">{t.to}:</span>
                    <span className="text-gray-900">{email.sentTo}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 font-medium w-16">{t.sentAt}:</span>
                    <span className="text-gray-900">{formatEmailDateTime(email.sentAt)}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 font-medium w-16">{t.subject}:</span>
                    <span className="text-gray-900">{email.subject}</span>
                  </div>
                </div>
              </div>
              
              {/* Email Body */}
              <div className="text-sm text-gray-700">
                {/* biome-ignore lint/security/noDangerouslySetInnerHtml: content sanitized with DOMPurify.sanitize(email.emailBody) */}
                <div 
                  className={`${!isContentExpanded && shouldShowMoreButton ? 'max-h-40 overflow-hidden' : ''}`}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.emailBody) }}
                />
                
                {/* Show More/Less Button - only show if content is actually long */}
                {shouldShowMoreButton && (
                  <div className="mt-3 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleEmailContentExpansion(emailKey)
                      }}
                      className="px-4 py-2 text-sm text-blue-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      {isContentExpanded ? t.showLess : t.showMore}
                      <ChevronDown className={`h-4 w-4 inline ml-1 transition-transform duration-200 ${
                        isContentExpanded ? 'rotate-180' : 'rotate-0'
                      }`} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Helper function to format audit values with proper display text and date formatting
  const formatAuditDisplayValue = (value: any, fieldConfig: any) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    // Date / DateTime handling — branch off ColumnTypeCode first so we honour the
    // explicit field-config intent before falling back to heuristics on the value
    // string. The previous version collapsed both 'Date' and 'DateTime' into the
    // same date-only path (it split on 'T' regardless of column type), which made
    // fields like FirstCommunicationSent show only the date even though the column
    // is configured as DateTime. The new branching:
    //   ColumnTypeCode === 'DateTime' → date + time, converted to the user's local
    //                                   timezone via toLocaleString so a UTC ISO
    //                                   value like "+00:00" renders in ET/PT/etc.
    //   ColumnTypeCode === 'Date'      → date-only, stripped from the ISO leading.
    //   No explicit type but value looks ISO-shaped → fall back to date-only (legacy
    //                                                  behaviour for safety on rows
    //                                                  without field-config metadata).
    const columnType = fieldConfig?.columnTypeCode?.toLowerCase();
    const isDateTimeField = columnType === 'datetime';
    const isDateField =
      columnType === 'date' ||
      // Heuristic-only fallback (when the config didn't tell us the type): treat as
      // date-only. We deliberately don't treat name-includes-'date' as DateTime
      // because most columns named "*Date" are date-only by the existing convention.
      (!columnType && (
        fieldConfig?.columnCode?.toLowerCase().includes('date') ||
        fieldConfig?.columnNameEnglish?.toLowerCase().includes('date') ||
        fieldConfig?.columnNameFrench?.toLowerCase().includes('date') ||
        (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/))
      ));

    if (isDateTimeField && typeof value === 'string') {
      // Convert to local time. Time format is locale-aware: English uses 12-hour
      // with AM/PM (e.g. "1:30 PM"), French uses 24-hour (e.g. "13:30"). This
      // matches the operator expectation in each region and stays consistent with
      // the outer formatDateTime used in the record header.
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) {
        return parsed.toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: language !== 'fr',
        });
      }
      return value;
    }

    if (isDateField && typeof value === 'string') {
      // Extract date part from ISO string (e.g., "2025-04-02T00:00:00+00:00" -> "2025-04-02")
      if (value.includes('T')) {
        return value.split('T')[0];
      }
      // Handle date-only strings that might have extra characters
      const dateMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        return dateMatch[1];
      }
      return value;
    }

    // Handle TextList fields with custom values
    if (fieldConfig?.columnTypeCode?.toLowerCase() === 'textlist' && Array.isArray(fieldConfig.customValues)) {
      if (typeof value === 'string') {
        // For TextList, the value might be a single code or comma-separated codes
        const codes = value.split(',').map(code => code.trim());
        const displayTexts = codes.map(code => {
          const customValue = fieldConfig.customValues.find((cv: any) => cv.code === code);
          if (customValue) {
            return language === 'fr' ? (customValue.titleFrench || customValue.titleEnglish || code) : (customValue.titleEnglish || code);
          }
          return code;
        });
        return displayTexts.join(', ');
      }
    }

    // Handle other fields with custom values
    if (Array.isArray(fieldConfig?.customValues) && fieldConfig.customValues.length > 0) {
      const customValue = fieldConfig.customValues.find((cv: any) => cv.code === String(value));
      if (customValue) {
        return language === 'fr' ? (customValue.titleFrench || customValue.titleEnglish || value) : (customValue.titleEnglish || value);
      }
    }

    // Resolve global code-value tables (e.g. wfh.CodePreferredLanguage, org.CodeLineOfBusiness)
    // so audit rows display "French" instead of the raw stored code "fr-CA". Honour
    // codeValueLookupType when present — most fields are byCode, but some use byId.
    // Falls through (returns formatAuditValue) when the code isn't found so we never
    // hide the original value if the lookup is unknown to the service.
    if (fieldConfig?.codeValueSource && codeValues?.isReady) {
      const valueStr = String(value);
      let lookupText: string | undefined;
      if (fieldConfig.codeValueLookupType === 'byId') {
        const id = parseInt(valueStr, 10);
        if (!isNaN(id)) {
          lookupText = codeValues.getCodeValueText(id, fieldConfig.codeValueSource);
        }
      } else {
        lookupText = codeValues.getCodeValueTextByCode(valueStr, fieldConfig.codeValueSource);
      }
      if (lookupText) return lookupText;
    }

    // Handle boolean fields
    if (fieldConfig?.columnTypeCode?.toLowerCase() === 'boolean') {
      if (value === true) {
        return t.true;
      } else if (value === false) {
        return t.false;
      }
    }

    return formatAuditValue(value);
  }

  // Helper function to get color for a value
  const getValueColor = (value: any, fieldConfig: any) => {
    // Don't show colors for null/empty values (which display as "-")
    if (value === null || value === undefined || value === '') {
      return null;
    }

    // Check if field configuration itself has a color
    if (fieldConfig?.color) {
      return fieldConfig.color;
    }

    // Check if matching custom value has a color
    if (Array.isArray(fieldConfig?.customValues) && fieldConfig.customValues.length > 0) {
      const customValue = fieldConfig.customValues.find((cv: any) => cv.code === String(value));
      if (customValue?.color) {
        return customValue.color;
      }
    }

    return null;
  }

  // Helper function to parse checklist JSON safely
  const parseChecklistValue = (value: string): Array<{Title: string, IsCompleted: boolean, Order: number, IsDeleted: boolean}> | null => {
    if (!value || typeof value !== 'string') return null;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  // Helper function to render checklist comparison
  const renderChecklistComparison = (oldValue: string, newValue: string) => {
    const oldItems = parseChecklistValue(oldValue) || [];
    const newItems = parseChecklistValue(newValue) || [];

    // Create a map of items by title for easier comparison
    // Filter out deleted items from old array for matching purposes
    const oldItemsMap = new Map(
      oldItems.filter(item => !item.IsDeleted).map(item => [item.Title, item])
    );
    const newItemsMap = new Map(
      newItems.filter(item => !item.IsDeleted).map(item => [item.Title, item])
    );

    // Get all unique titles
    const allTitles = new Set([
      ...oldItems.filter(item => !item.IsDeleted).map(i => i.Title), 
      ...newItems.filter(item => !item.IsDeleted).map(i => i.Title)
    ]);
    
    // Sort by the order from newItems, fallback to oldItems
    const sortedTitles = Array.from(allTitles).sort((a, b) => {
      const aItem = newItemsMap.get(a) || oldItemsMap.get(a);
      const bItem = newItemsMap.get(b) || oldItemsMap.get(b);
      return (aItem?.Order ?? 999) - (bItem?.Order ?? 999);
    });

    return (
      <div className="space-y-2">
        {sortedTitles.map(title => {
          const oldItem = oldItemsMap.get(title);
          const newItem = newItemsMap.get(title);

          // Item was added
          if (!oldItem && newItem) {
            return (
              <div key={`add-${title}`} className="bg-gray-100 rounded-full px-4 py-2">
                <div className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-3">
                    <div className="text-gray-600 text-xs text-right">-</div>
                  </div>
                  <div className="col-span-2 flex justify-center">
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="col-span-7">
                    <div className="flex items-center gap-1.5">
                      <svg className="h-3.5 w-3.5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                      {newItem.IsCompleted && (
                        <svg className="h-3.5 w-3.5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      <span className={`text-xs ${newItem.IsDeleted ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                        {newItem.Title}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          // Item was removed or deleted
          if (oldItem && !newItem) {
            return (
              <div key={`remove-${title}`} className="bg-gray-100 rounded-full px-4 py-2">
                <div className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <svg className="h-3.5 w-3.5 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      {oldItem.IsCompleted && (
                        <svg className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      <span className="text-xs line-through text-gray-400">
                        {oldItem.Title}
                      </span>
                    </div>
                  </div>
                  <div className="col-span-2 flex justify-center">
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="col-span-7">
                    <div className="text-gray-600 text-xs">-</div>
                  </div>
                </div>
              </div>
            );
          }

          // Item exists in both - check for changes
          if (oldItem && newItem) {
            const titleChanged = oldItem.Title !== newItem.Title;
            const completedChanged = oldItem.IsCompleted !== newItem.IsCompleted;
            const deletedChanged = oldItem.IsDeleted !== newItem.IsDeleted;
            const hasChanges = titleChanged || completedChanged || deletedChanged;

            if (!hasChanges) {
              // No changes - don't render
              return null;
            }

            // Item has changes - show side by side
            return (
              <div key={`change-${title}`} className="bg-gray-100 rounded-full px-4 py-2">
                <div className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-3">
                    <div className="flex items-center justify-end gap-1.5">
                      {oldItem.IsCompleted && (
                        <svg className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      <span className={`text-xs truncate ${oldItem.IsDeleted ? 'line-through text-gray-400' : 'text-gray-600'}`}>
                        {oldItem.Title}
                      </span>
                    </div>
                  </div>
                  <div className="col-span-2 flex justify-center">
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="col-span-7">
                    <div className="flex items-center gap-1.5">
                      {newItem.IsCompleted && (
                        <svg className="h-3.5 w-3.5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      <span className={`text-xs truncate ${newItem.IsDeleted ? 'line-through text-gray-400' : 'text-gray-900'} ${titleChanged ? 'bg-yellow-200 px-1 rounded' : ''}`}>
                        {newItem.Title}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          return null;
        })}
      </div>
    );
  }

  const renderFieldChange = (difference: UpdateEntry, entityType?: string) => {
    if (!difference || !difference.fieldName) {
      return null;
    }
    
    // Exclude LastModifiedDateTime fields as they're not meaningful to users
    if (difference.fieldName.toLowerCase().includes('lastmodifieddatetime')) {
      return null;
    }
    
    // Special handling for Stage field - always show it even if no field configuration exists
    const isStageFieldCheck = isStageField(difference.fieldName);
    
    let fieldDisplayName: string | null = null;
    let fieldConfig: any = null;
    
    if (isStageFieldCheck) {
      // For stage fields, use a localized display name
      fieldDisplayName = t.stage;
      // Don't use field config for stages as we'll handle the values specially
    } else {
      // Try to resolve field display name using workflow configuration
      fieldDisplayName = resolveFieldDisplayName(difference.fieldName, configuration, language, difference.propertyPath, entityType);

      // If field is not found in configuration, don't render it
      if (fieldDisplayName === null) {
        return null;
      }
      
      // Find the field configuration for this field
      // Try multiple field matching strategies to ensure we find the right configuration
      fieldConfig = configuration?.fieldsInUse?.find(f => 
        f.fieldConfigurationId === difference.fieldName || 
        f.columnCode === difference.fieldName ||
        f.columnCode?.toLowerCase() === difference.fieldName?.toLowerCase() ||
        f.fieldConfigurationId?.toLowerCase() === difference.fieldName?.toLowerCase()
      );
    }

    // Special handling for Checklist fields
    const isChecklistField = fieldConfig?.columnTypeCode?.toLowerCase() === 'checklist';
    
    if (isChecklistField) {
      return (
        <div key={`${difference.fieldName}-${difference.oldValue}-${difference.newValue}`} className="bg-gray-50 rounded-lg px-4 py-3 mb-2 border border-gray-200">
          <div className="text-gray-700 text-sm font-medium mb-3" title={fieldDisplayName}>
            {highlightSearchMatch(fieldDisplayName, searchTerm)}
          </div>
          {renderChecklistComparison(difference.oldValue as string, difference.newValue as string)}
        </div>
      );
    }

    // Format old and new values with proper display text
    let oldDisplayValue: string;
    let newDisplayValue: string;
    let oldColor: string | null = null;
    let newColor: string | null = null;
    
    if (isStageFieldCheck) {
      // For stage fields, resolve stage titles from stageConfigurations
      oldDisplayValue = difference.oldValue ? resolveStageTitle(String(difference.oldValue), configuration, language) : '-';
      newDisplayValue = difference.newValue ? resolveStageTitle(String(difference.newValue), configuration, language) : '-';
      // Stage fields don't have colors in this context
    } else if (entityType === 'ClientRequest') {
      oldDisplayValue = (difference.oldValue != null
        ? resolveClientRequestFieldValue(difference.fieldName, String(difference.oldValue), language)
        : null) ?? formatAuditDisplayValue(difference.oldValue, fieldConfig);
      newDisplayValue = (difference.newValue != null
        ? resolveClientRequestFieldValue(difference.fieldName, String(difference.newValue), language)
        : null) ?? formatAuditDisplayValue(difference.newValue, fieldConfig);
    } else {
      // Use existing logic for other fields
      oldDisplayValue = formatAuditDisplayValue(difference.oldValue, fieldConfig);
      newDisplayValue = formatAuditDisplayValue(difference.newValue, fieldConfig);
      // Get colors for old and new values
      oldColor = getValueColor(difference.oldValue, fieldConfig);
      newColor = getValueColor(difference.newValue, fieldConfig);
    }
    
    return (
      <div key={`${difference.fieldName}-${difference.oldValue}-${difference.newValue}`} className="bg-gray-100 rounded-full px-4 py-3 mb-2">
        <div className="grid grid-cols-12 gap-3 items-center">
          <div className="col-span-4">
            <div className="text-gray-700 text-sm font-medium truncate text-left" title={fieldDisplayName}>
              {highlightSearchMatch(fieldDisplayName, searchTerm)}
            </div>
          </div>
          <div className="col-span-3">
            <div className="flex items-center justify-end gap-2">
              {oldColor && (
                <div 
                  className="w-4 h-4 rounded-sm border border-gray-300 flex-shrink-0"
                  style={{ backgroundColor: oldColor }}
                  title={`Color: ${oldColor}`}
                />
              )}
              <div className="text-gray-600 text-xs truncate text-right" title={oldDisplayValue}>
                {oldDisplayValue}
              </div>
            </div>
          </div>
          <div className="col-span-2 flex justify-center">
            <ChevronRight className="h-4 w-4 text-gray-400" />
          </div>
          <div className="col-span-3">
            <div className="flex items-center justify-start gap-2">
              {newColor && (
                <div 
                  className="w-4 h-4 rounded-sm border border-gray-300 flex-shrink-0"
                  style={{ backgroundColor: newColor }}
                  title={`Color: ${newColor}`}
                />
              )}
              <div className="text-gray-600 text-xs truncate text-left" title={newDisplayValue}>
                {newDisplayValue}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderAuditRecord = (record: AuditHistoryDocument, index: number) => {
    if (!record || !record.id) {
      return null;
    }

    // Check if there are any valid field changes to display
    const hasValidChanges = record.differences && Array.isArray(record.differences) && record.differences.length > 0 && 
      record.differences.some(difference => {
        if (!difference || !difference.fieldName) return false;
        
        // Exclude LastModifiedDateTime fields as they're not meaningful to users
        if (difference.fieldName.toLowerCase().includes('lastmodifieddatetime')) return false;
        
        // Always show stage fields
        if (isStageField(difference.fieldName)) return true;
        
        // For other fields, check if they have field configurations
        const fieldDisplayName = resolveFieldDisplayName(difference.fieldName, configuration, language, difference.propertyPath, record.entityType);
        return fieldDisplayName !== null;
      });

    // If there are no valid changes and no comment, don't render this record at all
    if (!hasValidChanges && !record.comment) {
      return null;
    }

    // Resolve stage title from workflow configuration using EntityStatusCode
    const stageTitle = resolveStageTitle(record.entityStatusCode, configuration, language, record.entityType)
    const isExpanded = expandedRecords.has(record.id)
    
    // Format date and time with timezone (reusing same logic as automation emails)
    const formatDateTime = (dateTimeString: string) => {
      if (!dateTimeString) return ''
      
      // Handle timezone properly - don't add Z if it already has timezone info
      let date: Date
      if (dateTimeString.endsWith('Z') || dateTimeString.match(/[+-]\d{2}:\d{2}$/)) {
        date = new Date(dateTimeString)
      } else {
        date = new Date(`${dateTimeString}Z`)
      }
      
      if (isNaN(date.getTime())) return dateTimeString
      
      const locale = language === 'fr' ? 'fr-FR' : 'en-US'
      const dateStr = date.toLocaleDateString(locale)
      // Locale-aware time format: English uses 12-hour with AM/PM (e.g. "1:30 PM"),
      // French uses 24-hour (e.g. "13:30"). Matches the inner formatAuditDisplayValue
      // DateTime branch and the outer formatDateTime helper used elsewhere in the panel.
      const timeStr = date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: language !== 'fr'
      })
      const timezone = getTimezoneAbbreviation(date)
      return `${dateStr} ${timeStr} (${timezone})`
    }
    
    // Actor taxonomy: "GW Automation" (+ historical "GW Automation - <reason>" suffixes) =
    // an automation rule made the change; "System" (+ historical system suffixes) = platform
    // plumbing (sync, timers, stage handover); anything else = a person.
    const displayName = getUserDisplayName(record)
    const isAutomatedUser = !!displayName?.startsWith('GW Automation')
    const isSystemUser = displayName === 'System'
    const isClientRequest = record.entityType === 'ClientRequest'
    const clientRequestIcon = clientRequestIconMap[record.entityStatusCode] ?? clientRequestDefaultIcon
    
    return (
      <div key={record.id} className="border-b border-gray-100 last:border-b-0">
        {/* Record content */}
        <div className="flex items-start space-x-3 p-3 hover:bg-gray-50">
          {/* Person, Automation, or System icon */}
          <div className="flex-shrink-0 mt-0.5">
            {isAutomatedUser ? (
              <Zap className="h-4 w-4 text-blue-500" />
            ) : isSystemUser ? (
              <Settings className="h-4 w-4 text-gray-400" />
            ) : (
              // Person edit → person icon for every entity (incl. ClientRequest), so the
              // actor icon is consistent across tabs. (ClientRequest previously showed a
              // per-request-type calendar icon here even for human edits.)
              <User className="h-4 w-4 text-gray-500" />
            )}
          </div>
          
          {/* Record details */}
          <div className="flex-1 min-w-0">
            {/* Header row with updated layout and smaller font */}
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => toggleRecordExpansion(record.id)}
              title={isExpanded ? t.clickToCollapse : t.clickToExpand}
            >
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <span className="text-sm text-gray-900 font-medium truncate">{displayName || t.unknownUser}</span>
                <span className="text-gray-400">•</span>
                <span className="text-sm text-gray-500 whitespace-nowrap">
                  {record.createdDateTime ? formatDateTime(record.createdDateTime) : t.unknownDate}
                </span>
                <span className="text-gray-400">•</span>
                <span className="text-xs text-blue-600 font-medium">{stageTitle}</span>
              </div>
              
              <div className="flex items-center ml-4">
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                  isExpanded ? 'rotate-180' : 'rotate-0'
                }`} />
              </div>
            </div>
            
            {/* Expanded details */}
            <div 
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="mt-2 pl-1">
                {/* Field changes */}
                {hasValidChanges && (
                  <div>
                    {/* Field changes — collapse nested-object diffs (e.g. AssignedToUser leaves)
                        into one row per parent before rendering. See collapseNestedDiffs. */}
                    {collapseNestedDiffs(record.differences)
                      .map((difference, diffIndex) => renderFieldChange(difference, record.entityType))
                      .filter(fieldChange => fieldChange !== null)
                      .map((fieldChange, diffIndex) => (
                        <div key={diffIndex}>
                          {fieldChange}
                        </div>
                      ))}
                  </div>
                )}
                
                {/* Change reason if available */}
                {record.comment && (
                  <div className="mt-3 p-4 bg-yellow-50 rounded-md border border-yellow-200">
                    <div className="text-sm font-semibold text-yellow-900 mb-2">{t.changeReason}:</div>
                    <div className="text-sm text-yellow-800 leading-relaxed">{record.comment}</div>
                  </div>
                )}
                
                {/* Additional info */}
                {record.additionalInfo && Object.keys(record.additionalInfo).length > 0 && (
                  <div className="mt-2 text-xs text-gray-400">
                    {t.additionalInfo}: {JSON.stringify(record.additionalInfo)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!isOpen) return null

  return (
    <>
      {/* Custom animations styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          .animate-fade-in-up {
            animation: fadeInUp 0.4s ease-out;
          }
          
          .animate-fade-in {
            animation: fadeIn 0.3s ease-out;
          }
        `
      }} />
      
      <div 
        ref={panelRef} 
        className="fixed inset-y-0 right-0 w-[800px] bg-white shadow-xl border-l border-gray-200 z-50 flex flex-col font-roboto transition-all duration-300 ease-out"
        style={{
          transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
          opacity: isVisible ? 1 : 0
        }}
      >
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex">
          <button
            onClick={() => setActiveTab('history')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span>{t.changeHistory}</span>
          </button>
          {showAutomationEmailsTab && (
            <button
              onClick={() => setActiveTab('automation')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'automation'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{t.automationEmails}</span>
            </button>
          )}
        </nav>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {activeTab === 'history'
              ? `${t.historyOf} ${itemTitle || t.candidate}`
              : `${t.automationEmailHistoryOf} ${itemTitle || t.candidate}`
            }
          </h2>
        </div>
        <div className="flex items-center gap-1">
          {activeTab === 'history' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refreshHistory()}
              disabled={loading}
              className="h-8 w-8 p-0"
              aria-label={t.refreshChangeHistory}
              title={t.refresh}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          )}
          {activeTab === 'automation' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refreshEmails()}
              disabled={emailsLoading}
              className="h-8 w-8 p-0"
              aria-label={t.refreshAutomationEmails}
              title={t.refresh}
            >
              <RefreshCw className={`h-4 w-4 ${emailsLoading ? 'animate-spin' : ''}`} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
            aria-label={t.close}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Search and Filters - Only show for History tab */}
      {activeTab === 'history' && renderFilterSection(
        t.searchChanges,
        searchTerm,
        handleSearch,
        availableStages,
        selectedStatus,
        setSelectedStatus,
        t.allStages,
        availableUsers,
        selectedUser,
        setSelectedUser,
        t.allUsers,
        selectedDateFilter,
        setSelectedDateFilter,
        customStartDate,
        setCustomStartDate,
        customEndDate,
        setCustomEndDate,
        !!(selectedStatus || selectedUser || selectedDateFilter !== 'all' || searchTerm),
        clearHistoryFilters
      )}

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          {activeTab === 'history' ? (
            <div>
              {loading && (!records || records.length === 0) ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">{t.loadingHistory}</span>
                </div>
              ) : (!visibleRecords || visibleRecords.length === 0) ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    {t.noChangesFound}
                  </p>
                  {(searchTerm || selectedStatus || selectedUser || selectedDateFilter !== 'all') && (
                    <p className="text-sm text-gray-400 mt-1">
                      {t.adjustSearchFilters}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className="text-xs text-gray-500 px-4 py-2 bg-gray-50 border-b border-gray-200">
                    {searchTerm || selectedStatus || selectedUser || selectedDateFilter !== 'all'
                      ? `${displayableRecords.length} ${displayableRecords.length !== 1 
                          ? t.changesFound
                          : t.changeFound
                        }`
                      : `${t.showing} ${visibleRecords.length} ${t.of} ${displayableRecords.length} ${displayableRecords.length !== 1 ? t.changes : t.change}`
                    }
                  </div>
                  
                  {/* V2: Show grouped or flat view based on number of WorkOrder groups */}
                  {groupedRecords.length > 1 ? (
                    // Multiple WorkOrders - show grouped view
                    <div>
                      {groupedRecords.map((group) => {
                        const isGroupExpanded = expandedWorkOrders.has(group.workOrderNumber)
                        // Filter changes for this group based on filters
                        const groupFilteredRecords = group.changes.filter(record => {
                          // Apply same filters as visibleRecords
                          if (selectedStatus && record.entityStatusCode !== selectedStatus) return false
                          if (selectedUser && getUserDisplayName(record) !== selectedUser) return false
                          // Mirror the flat-path date casing fallback (see
                          // recordCreatedAt in filteredRecords). The VMS audit
                          // endpoint returns records wrapped by a server-side
                          // shim with PascalCase keys; without the fallback the
                          // grouped view silently treated every record as
                          // undated and kept it regardless of the date filter.
                          const groupRecordCreatedAt = (record as any).createdDateTime ?? (record as any).CreatedDateTime
                          if (selectedDateFilter !== 'all') {
                            if (!groupRecordCreatedAt) return false
                            if (!isWithinDateRange(groupRecordCreatedAt, selectedDateFilter, customStartDate, customEndDate)) return false
                          }
                          if (searchTerm) {
                            const searchLower = searchTerm.toLowerCase()
                            
                            // Search in field names (both raw field names and resolved display names)
                            const resolveValueForSearch = (value: any, fieldName: string) => {
                              if (value == null) return ''
                              const strValue = String(value)
                              if (isStageField(fieldName)) return resolveStageTitle(strValue, configuration, language)
                              if (record.entityType === 'ClientRequest') {
                                return resolveClientRequestFieldValue(fieldName, strValue, language) ?? (strValue.includes('T') ? strValue.split('T')[0] : strValue)
                              }
                              return strValue.includes('T') ? strValue.split('T')[0] : strValue
                            }
                            const hasMatchingField = record.differences?.some(difference => {
                              // Check raw field name (handles "Stage" and other fields not in config)
                              const rawFieldNameMatch = difference.fieldName?.toLowerCase().includes(searchLower)

                              // Check resolved display name (handles localized field names)
                              const fieldDisplayName = resolveFieldDisplayName(difference.fieldName, configuration, language, difference.propertyPath, record.entityType)
                              const displayNameMatch = fieldDisplayName && fieldDisplayName.toLowerCase().includes(searchLower)

                              // Check old and new values using resolved display strings
                              const oldValueMatch = difference.oldValue != null && resolveValueForSearch(difference.oldValue, difference.fieldName).toLowerCase().includes(searchLower)
                              const newValueMatch = difference.newValue != null && resolveValueForSearch(difference.newValue, difference.fieldName).toLowerCase().includes(searchLower)

                              return rawFieldNameMatch || displayNameMatch || oldValueMatch || newValueMatch
                            })

                            const hasMatchingUser = getUserDisplayName(record)?.toLowerCase().includes(searchLower)
                            const stageTitle = resolveStageTitle(record.entityStatusCode, configuration, language, record.entityType)
                            const hasMatchingStatus = stageTitle.toLowerCase().includes(searchLower)
                            const hasMatchingComment = record.comment?.toLowerCase().includes(searchLower)
                            
                            return hasMatchingField || hasMatchingUser || hasMatchingStatus || hasMatchingComment
                          }
                          return true
                        })
                        
                        if (groupFilteredRecords.length === 0) return null
                        
                        return (
                          <div key={group.workOrderNumber} className="border-b border-gray-200">
                            {/* WorkOrder Group Header */}
                            <div 
                              className="flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 cursor-pointer"
                              onClick={() => toggleWorkOrderGroupExpansion(group.workOrderNumber)}
                            >
                              <div className="flex items-center space-x-2">
                                <ChevronRight className={`h-4 w-4 text-gray-600 transition-transform duration-200 ${
                                  isGroupExpanded ? 'rotate-90' : 'rotate-0'
                                }`} />
                                <span className="text-sm font-semibold text-gray-900">
                                  {`WO: ${group.workOrderNumber}`}
                                </span>
                                {(group.startDate || group.endDate) && (
                                  <>
                                    <span className="text-gray-400">•</span>
                                    <span className="text-sm text-gray-600">
                                      {group.startDate ? new Date(group.startDate).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US') : ''}
                                      {group.startDate && group.endDate && ' - '}
                                      {group.endDate ? new Date(group.endDate).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US') : ''}
                                    </span>
                                  </>
                                )}
                              </div>
                              <span className="text-xs text-gray-500">
                                {groupFilteredRecords.length} {groupFilteredRecords.length !== 1 ? t.changes : t.change}
                              </span>
                            </div>
                            
                            {/* Group Changes */}
                            <div 
                              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                isGroupExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
                              }`}
                            >
                              {groupFilteredRecords.filter(record => record && record.id).map((record, index) => (
                                <div key={record.id}>
                                  {renderAuditRecord(record, index)}
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    // Single or no WorkOrder - show flat view (existing logic)
                    <div>
                      {visibleRecords.filter(record => record && record.id).map((record, index) => (
                        <div key={record.id}>
                          {renderAuditRecord(record, index)}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Show more button - only for flat view */}
                  {groupedRecords.length <= 1 && !searchTerm && !selectedStatus && !selectedUser && selectedDateFilter === 'all' &&
                   displayableRecords.length > visibleRecords.length && (
                    <div className="text-center py-3 bg-gray-50 border-t border-gray-200">
                      <Button
                        variant="outline"
                        onClick={handleShowMore}
                        size="sm"
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        {`${t.showMore} ${Math.min(5, displayableRecords.length - visibleRecords.length)}`}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            // Automation Emails Tab Content
            <div>
              {/* Search and Filters for emails */}
              {renderFilterSection(
                t.searchEmailSubject,
                emailSearchTerm,
                setEmailSearchTerm,
                availableStages,
                selectedEmailStage,
                setSelectedEmailStage,
                t.allStages,
                availableEmailRecipients,
                selectedEmailRecipient,
                setSelectedEmailRecipient,
                t.selectRecipient,
                selectedEmailDateFilter,
                setSelectedEmailDateFilter,
                customEmailStartDate,
                setCustomEmailStartDate,
                customEmailEndDate,
                setCustomEmailEndDate,
                !!(selectedEmailStage || selectedEmailRecipient || selectedEmailDateFilter !== 'all' || emailSearchTerm),
                clearEmailFilters
              )}

              {emailsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">{t.loadingEmails}</span>
                </div>
              ) : filteredEmails.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {t.noEmailsFound}
                  </p>
                  {emailSearchTerm && (
                    <p className="text-sm text-gray-400 mt-1">
                      {t.adjustSearchFilters}
                    </p>
                  )}
                  {(selectedEmailStage || selectedEmailRecipient || selectedEmailDateFilter !== 'all') && (
                    <p className="text-sm text-gray-400 mt-1">
                      {t.adjustSearchFilters}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className="text-xs text-gray-500 px-4 py-2 bg-gray-50 border-b border-gray-200">
                    {filteredEmails.length} {filteredEmails.length !== 1 ? 'emails' : 'email'}
                  </div>
                  
                  {/* Show grouped view when workOrders are available */}
                  {emailWorkOrders.length >= 1 ? (
                    // Grouped view by WorkOrder
                    <div>
                      {emailWorkOrders.map((workOrder) => {
                        const isGroupExpanded = expandedEmailWorkOrders.has(workOrder.workOrderNumber)
                        // Filter emails for this group based on filters
                        const groupFilteredEmails = workOrder.emails.filter(email => {
                          // Stage filter
                          if (selectedEmailStage && email.stage !== selectedEmailStage) {
                            return false
                          }
                          
                          // Recipient filter
                          if (selectedEmailRecipient && email.sentTo !== selectedEmailRecipient) {
                            return false
                          }
                          
                          // Date filter
                          if (selectedEmailDateFilter !== 'all' && email.sentAt) {
                            if (!isWithinDateRange(email.sentAt, selectedEmailDateFilter, customEmailStartDate, customEmailEndDate)) {
                              return false
                            }
                          }
                          
                          // Search filter
                          if (emailSearchTerm) {
                            const searchLower = emailSearchTerm.toLowerCase()
                            return email.subject?.toLowerCase().includes(searchLower) ||
                                   email.sentTo?.toLowerCase().includes(searchLower) ||
                                   email.sentFrom?.toLowerCase().includes(searchLower) ||
                                   email.emailBody?.toLowerCase().includes(searchLower)
                          }
                          
                          return true
                        })
                        
                        if (groupFilteredEmails.length === 0) return null
                        
                        return (
                          <div key={workOrder.workOrderNumber} className="border-b border-gray-200">
                            {/* WorkOrder Group Header */}
                            <div 
                              className="flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 cursor-pointer"
                              onClick={() => toggleEmailWorkOrderGroupExpansion(workOrder.workOrderNumber)}
                            >
                              <div className="flex items-center space-x-2">
                                <ChevronRight className={`h-4 w-4 text-gray-600 transition-transform duration-200 ${
                                  isGroupExpanded ? 'rotate-90' : ''
                                }`} />
                                <span className="text-sm font-medium text-gray-900">{workOrder.workOrderNumber}</span>
                              </div>
                              <span className="text-xs text-gray-500">
                                {groupFilteredEmails.length} {groupFilteredEmails.length !== 1 ? 'emails' : 'email'}
                              </span>
                            </div>
                            
                            {/* Group Emails */}
                            <div 
                              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                isGroupExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
                              }`}
                            >
                              {groupFilteredEmails.map((email, index) => renderEmailItem(email, index, workOrder.workOrderNumber))}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    // No WorkOrders available - show flat view (fallback)
                    <div>
                      {filteredEmails.map((email, index) => renderEmailItem(email, index))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Load More - Only show for History tab */}
      {hasMore && activeTab === 'history' && (
        <div className="p-4 border-t border-gray-200">
          <Button
            onClick={handleLoadMore}
            disabled={loading}
            variant="outline"
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t.loading}
              </>
            ) : (
              t.loadMore
            )}
          </Button>
        </div>
      )}
    </div>
    </>
  )
}
